/**
 * Top Selling Articles Report Page
 */

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, TrendingUp } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'

interface TopArticle {
  id: number
  code: string
  description: string
  quantity_sold: number
  total_revenue: number
}

export function TopArticlesReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: topArticles, isLoading } = useQuery({
    queryKey: ['reports-top-articles'],
    queryFn: async () => {
      const response = await api.get('/reports/sales/top-articles/?limit=50')
      return response.data as TopArticle[]
    },
  })

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.topSellingArticles')}</h1>
          <p className="text-sm text-muted-foreground">{t('reports.thisMonth')}</p>
        </div>
        <ExportButtons
          data={topArticles || []}
          filename="articulos-mas-vendidos"
          columns={[
            { key: 'code', header: t('articles.code') },
            { key: 'description', header: t('articles.description') },
            { key: 'quantity_sold', header: t('billing.quantity'), format: (v) => String(v) },
            { key: 'total_revenue', header: t('reports.total'), format: (v) => formatCurrency(v as number) },
          ]}
        />
      </div>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-500" />
            {t('reports.misc.salesRanking')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : topArticles && topArticles.length > 0 ? (
            <div className="space-y-2">
              {topArticles.map((article, index) => (
                <div
                  key={article.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 active:bg-muted cursor-pointer touch-manipulation"
                  onClick={() => navigate(`/articles/${article.id}`)}
                >
                  <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center shrink-0">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{article.code}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {article.description}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-primary">{article.quantity_sold} uds</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(article.total_revenue)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t('reports.noDataAvailable')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
